import * as R from 'ramda'

import { BlockModeEnum } from 'gql/__generated__/graphql'
import type { Block, IBlockState, IProjectContext, MapType } from 'services/Store/Project/types'

import { getBlock, getBlockElementsState, getBlocks, getSection } from '..'
import { getSectionId } from '..'
import { getBlocksState, getBlockState } from './block'
import { getSectionState } from './section'

export const getStartTime = (state: IProjectContext, id?: string) =>
  getSectionState(state, { id: id || getSectionId(state) })?.startTime || 0

export const getEndTime = (state: IProjectContext, id?: string) =>
  getSectionState(state, { id: id || getSectionId(state) })?.endTime || 0

export const getTotalQuestions = (state: IProjectContext, id?: string) =>
  R.compose<[Block[]], Block[], number>(
    R.length,
    R.filter(R.propSatisfies((mode) => mode === BlockModeEnum.questions, 'mode')),
  )(getBlocks(state, id || getSectionId(state)))

export const getDoneQuestions = (state: IProjectContext, id?: string) =>
  R.compose<[MapType<IBlockState>], IBlockState[], IBlockState[], number>(
    R.length,
    R.filter<IBlockState>(R.has('result')),
    R.values,
  )(getBlocksState(state, { sectionId: id || getSectionId(state) }))

export const getSuccessQuestions = (state: IProjectContext, id?: string) =>
  R.compose<[MapType<IBlockState>], IBlockState[], number>(
    R.reduce((acc, cur: IBlockState) => (cur.result?.isValid ? R.inc(acc) : acc), 0),
    R.values,
  )(getBlocksState(state, { sectionId: id || getSectionId(state) }))

export const getTestPercent = (state: IProjectContext, id?: string) =>
  getTestScore(state, id).percent

export const getTestScore = (state: IProjectContext, id?: string) => {
  const total = getTotalQuestions(state, id || getSectionId(state))
  const success = getSuccessQuestions(state, id || getSectionId(state)) || 0
  const scaled = success / total || 0
  const percent = Math.floor(scaled * 100)
  return {
    total,
    success,
    scaled,
    percent,
  }
}

export const getTestInfo = (state: IProjectContext, blockId: string) => {
  const block = getBlock(state, { id: blockId })
  const blockState = getBlockState(state, blockId)
  const elements = getBlockElementsState(state, blockId)

  if (!block) {
    return null
  }

  return {
    isMandatory: block.isMandatory,
    answerRequired: block.test?.answerRequired,
    currentTry: blockState.currentTry || 0,
    isReady: blockState.isReady,
    result: blockState.result,
    elements: Object.entries(elements).map(([elementId, elState]) => ({
      elementId,
      value: elState.value,
    })),
  }
}

export const getCurrentTry = (state: IProjectContext, id?: string) =>
  getSectionState(state, { id: id || getSectionId(state) })?.currentTry

export const getTestStartBlock = (state: IProjectContext, id?: string): Block | undefined =>
  R.find(R.propSatisfies((mode) => mode === BlockModeEnum.start, 'mode'))(
    getBlocks(state, id || getSectionId(state)),
  )

export const getTestEndBlock = (state: IProjectContext, id?: string): Block | undefined =>
  R.find(R.propSatisfies((mode) => mode === BlockModeEnum.end, 'mode'))(
    getBlocks(state, id || getSectionId(state)),
  )

export const getCanRetryTest = (state: IProjectContext) => {
  const testRetryCount = getSection(state)?.test?.testRetryCount || 0
  const isLimit = getSection(state)?.test?.testLimit || false
  const currentTry = getCurrentTry(state) || 0
  return !isLimit || (testRetryCount > 1 && currentTry < testRetryCount)
}

export const getIsSuccessResult = (state: IProjectContext, sectionId?: string) => {
  const section = getSection(state, { id: sectionId })
  const passPercent = getTestPercent(state, section?.id)
  const testThreshold = section?.test?.testThreshold || 0
  return passPercent >= testThreshold
}
